export const ALL_DENOMINATIONS = [5, 10, 20, 30, 50, 70, 100, 200, 500, 1000, 5000];
export const DENOMINATIONS_C = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function flatToGrid(flat) {
  return [flat.slice(0, 4), flat.slice(4, 8), flat.slice(8, 12), flat.slice(12, 16)];
}

function gridKey(grid) {
  return grid.flat().join(',');
}

function countAll(grid) {
  const counts = {};
  for (const row of grid) for (const c of row) counts[c] = (counts[c] || 0) + 1;
  return counts;
}

function getLines(grid) {
  const lines = [];
  for (let r = 0; r < 4; r++) lines.push([...grid[r]]);
  for (let c = 0; c < 4; c++) lines.push([grid[0][c], grid[1][c], grid[2][c], grid[3][c]]);
  lines.push([grid[0][0], grid[1][1], grid[2][2], grid[3][3]]);
  lines.push([grid[0][3], grid[1][2], grid[2][1], grid[3][0]]);
  return lines;
}

export function checkWinnerA(grid) {
  const counts = countAll(grid);
  for (const [num, cnt] of Object.entries(counts)) {
    if (cnt >= 3) return { isWinner: true, winningNumber: Number(num), count: cnt };
  }
  return { isWinner: false, winningNumber: null, count: 0 };
}

export function checkWinnerB(grid) {
  for (const line of getLines(grid)) {
    for (let i = 0; i <= line.length - 3; i++) {
      if (line[i] !== 0 && line[i] === line[i + 1] && line[i + 1] === line[i + 2]) {
        return { isWinner: true, winningNumber: line[i], count: 3, lineType: 'line' };
      }
    }
  }
  return { isWinner: false, winningNumber: null, count: 0 };
}

function generateWinnerCardA(winDenom, denoms, maxAttempts = 2000) {
  const others = denoms.filter(d => d !== winDenom);
  for (let a = 0; a < maxAttempts; a++) {
    const positions = shuffle([...Array(16).keys()]);
    const winPositions = positions.slice(0, 3);
    const restPositions = positions.slice(3);
    const flat = new Array(16);
    for (const p of winPositions) flat[p] = winDenom;

    const denomCounts = {};
    let valid = true;
    for (const p of restPositions) {
      const avail = others.filter(d => (denomCounts[d] || 0) < 2);
      if (avail.length === 0) { valid = false; break; }
      const pick = avail[Math.floor(Math.random() * avail.length)];
      flat[p] = pick;
      denomCounts[pick] = (denomCounts[pick] || 0) + 1;
    }
    if (!valid) continue;

    const grid = flatToGrid(flat);
    const result = checkWinnerA(grid);
    if (result.isWinner && result.winningNumber === winDenom) return grid;
  }
  return null;
}

function generateNonWinnerCardA(denoms, maxAttempts = 2000) {
  for (let a = 0; a < maxAttempts; a++) {
    const flat = [];
    const denomCounts = {};
    let valid = true;
    for (let i = 0; i < 16; i++) {
      const avail = denoms.filter(d => (denomCounts[d] || 0) < 2);
      if (avail.length === 0) { valid = false; break; }
      const pick = avail[Math.floor(Math.random() * avail.length)];
      flat.push(pick);
      denomCounts[pick] = (denomCounts[pick] || 0) + 1;
    }
    if (!valid) continue;
    const grid = flatToGrid(flat);
    if (!checkWinnerA(grid).isWinner) return grid;
  }
  return null;
}

function generateWinnerCardB(winDenom, denoms, maxAttempts = 3000) {
  const others = denoms.filter(d => d !== winDenom);
  const rows = [];
  for (let r = 0; r < 4; r++) rows.push([[r,0],[r,1],[r,2],[r,3]]);
  const cols = [];
  for (let c = 0; c < 4; c++) cols.push([[0,c],[1,c],[2,c],[3,c]]);
  const diags = [
    [[0,0],[1,1],[2,2],[3,3]],
    [[0,3],[1,2],[2,1],[3,0]],
  ];
  const lineTypes = shuffle([rows, cols, diags]);

  for (let a = 0; a < maxAttempts; a++) {
    const typeGroup = lineTypes[a % 3];
    const line = typeGroup[Math.floor(Math.random() * typeGroup.length)];
    const startIdx = Math.floor(Math.random() * (line.length - 2));
    const winCells = [line[startIdx], line[startIdx + 1], line[startIdx + 2]];
    const grid = Array.from({ length: 4 }, () => new Array(4).fill(0));
    for (const [r, c] of winCells) grid[r][c] = winDenom;

    const cellOrder = shuffle([...Array(16).keys()].filter(i => grid[Math.floor(i / 4)][i % 4] === 0));
    let valid = true;
    for (const ci of cellOrder) {
      const r = Math.floor(ci / 4), c = ci % 4;
      const avail = shuffle(others);
      let placed = false;
      for (const d of avail) {
        grid[r][c] = d;
        const tempResult = checkWinnerB(grid);
        if (!tempResult.isWinner || tempResult.winningNumber === winDenom) {
          placed = true;
          break;
        }
        grid[r][c] = 0;
      }
      if (!placed) { valid = false; break; }
    }
    if (!valid) continue;

    const result = checkWinnerB(grid);
    if (result.isWinner && result.winningNumber === winDenom) return grid;
  }
  return null;
}

export function checkWinnerC(grid) {
  const counts = countAll(grid);
  const winPairs = [];
  for (const [num, cnt] of Object.entries(counts)) {
    const numPairs = Math.floor(cnt / 2);
    for (let i = 0; i < numPairs; i++) winPairs.push(Number(num));
  }
  if (winPairs.length === 0) return { isWinner: false, winningNumbers: [], winPairs: [], winAmount: 0 };
  const total = winPairs.reduce((s, v) => s + v, 0);
  return { isWinner: true, winningNumbers: [...new Set(winPairs)], winPairs: winPairs.sort((a, b) => a - b), winAmount: total };
}

function findSplits(target, denoms) {
  const results = [];
  if (denoms.includes(target)) results.push([target]);
  for (let i = 0; i < denoms.length; i++) {
    const rem = target - denoms[i];
    if (rem >= denoms[i] && denoms.includes(rem)) results.push([denoms[i], rem]);
  }
  for (let i = 0; i < denoms.length; i++) {
    for (let j = i; j < denoms.length; j++) {
      const rem = target - denoms[i] - denoms[j];
      if (rem >= denoms[j] && rem > 0 && denoms.includes(rem)) results.push([denoms[i], denoms[j], rem]);
    }
  }
  return results;
}

function generateWinnerCardC(targetPrize, priorityFill, maxAttempts = 2000) {
  const splits = findSplits(targetPrize, DENOMINATIONS_C);
  if (splits.length === 0) return null;
  for (let a = 0; a < maxAttempts; a++) {
    const split = splits[Math.floor(Math.random() * splits.length)];
    const pairCounts = {};
    for (const d of split) pairCounts[d] = (pairCounts[d] || 0) + 1;
    const flat = new Array(16);
    const positions = shuffle([...Array(16).keys()]);
    let posIdx = 0;
    const usedDenoms = new Set();
    for (const [denom, count] of Object.entries(pairCounts)) {
      const d = Number(denom);
      usedDenoms.add(d);
      for (let i = 0; i < count * 2; i++) flat[positions[posIdx++]] = d;
    }
    const primary = shuffle((priorityFill || ALL_DENOMINATIONS).filter(d => !usedDenoms.has(d)));
    const overflow = shuffle(DENOMINATIONS_C.filter(d => !usedDenoms.has(d) && !(priorityFill || ALL_DENOMINATIONS).includes(d)));
    const fillDenoms = [...primary, ...overflow];
    const remaining = 16 - posIdx;
    if (fillDenoms.length < remaining) continue;
    for (let i = 0; i < remaining; i++) flat[positions[posIdx++]] = fillDenoms[i];
    const grid = flatToGrid(flat);
    const result = checkWinnerC(grid);
    if (result.isWinner && result.winAmount === targetPrize) return grid;
  }
  return null;
}

function generateNonWinnerCardC() {
  const picked = shuffle(DENOMINATIONS_C).slice(0, 16);
  return flatToGrid(picked);
}

function generateNonWinnerCardB(denoms, maxAttempts = 2000) {
  for (let a = 0; a < maxAttempts; a++) {
    const flat = [];
    for (let i = 0; i < 16; i++) {
      flat.push(denoms[Math.floor(Math.random() * denoms.length)]);
    }
    const grid = flatToGrid(flat);
    if (!checkWinnerB(grid).isWinner) return grid;
  }
  return null;
}

export function generateBundle(config) {
  const {
    productWinCondition,
    winnerConfig,
    cardCount = 100,
    denominations,
  } = config;

  const isTypeA = productWinCondition === 'any3';
  const isTypeC = productWinCondition === 'any2';

  const denoms = (() => {
    if (!denominations || !denominations.length) return isTypeC ? DENOMINATIONS_C : ALL_DENOMINATIONS;
    if (isTypeC) {
      const base = [...denominations].sort((a, b) => a - b);
      const extra = DENOMINATIONS_C.filter(d => !base.includes(d));
      return [...base, ...extra].sort((a, b) => a - b);
    }
    if (denominations.length >= 8) return [...denominations].sort((a, b) => a - b);
    return [...new Set([...denominations, ...ALL_DENOMINATIONS])].sort((a, b) => a - b);
  })();

  const denomsC = denominations?.length ? [...denominations].sort((a, b) => a - b) : (isTypeC ? DENOMINATIONS_C : ALL_DENOMINATIONS);
  const minPrize = isTypeC ? Math.min(...Object.keys(winnerConfig).map(Number).filter(d => d > 0), ...denoms.filter(d => d > 0)) : 0;

  const genWinner = isTypeC ? d => generateWinnerCardC(d, denomsC) : isTypeA ? d => generateWinnerCardA(d, denoms) : d => generateWinnerCardB(d, denoms);
  const genNonWinner = isTypeC ? generateNonWinnerCardC : isTypeA ? () => generateNonWinnerCardA(denoms) : () => generateNonWinnerCardB(denoms);
  const checkWinner = isTypeC ? checkWinnerC : isTypeA ? checkWinnerA : checkWinnerB;

  const cards = [];
  const usedKeys = new Set();

  for (const [denom, count] of Object.entries(winnerConfig)) {
    const denomNum = Number(denom);
    if (count <= 0) continue;
    for (let i = 0; i < count && cards.length < cardCount; i++) {
      let grid = null;
      let attempts = 0;
      while (attempts < 100) {
        grid = genWinner(denomNum);
        if (grid && !usedKeys.has(gridKey(grid))) break;
        grid = null;
        attempts++;
      }
      if (!grid) continue;
      usedKeys.add(gridKey(grid));
      const result = checkWinner(grid);
      const card = {
        grid,
        isWinner: true,
        winningNumber: result.winningNumber ?? result.winAmount,
        winAmount: result.winAmount ?? result.winningNumber,
      };
      if (result.winPairs) {
        card.winPairs = result.winPairs;
        card.winningNumbers = result.winningNumbers;
      }
      cards.push(card);
    }
  }

  const totalWinners = cards.length;
  const nonWinnerCount = cardCount - totalWinners;

  for (let i = 0; i < nonWinnerCount; i++) {
    if (isTypeC) {
      let grid = null;
      let attempts = 0;
      while (attempts < 100) {
        grid = generateWinnerCardC(minPrize || 20, denomsC);
        if (grid && !usedKeys.has(gridKey(grid))) break;
        grid = null;
        attempts++;
      }
      if (!grid) continue;
      usedKeys.add(gridKey(grid));
      const result = checkWinnerC(grid);
      cards.push({
        grid,
        isWinner: true,
        winningNumber: result.winAmount,
        winAmount: result.winAmount,
        winPairs: result.winPairs,
        winningNumbers: result.winningNumbers,
      });
    } else {
      let grid = null;
      let attempts = 0;
      while (attempts < 100) {
        grid = genNonWinner();
        if (grid && !usedKeys.has(gridKey(grid))) break;
        grid = null;
        attempts++;
      }
      if (!grid) continue;
      usedKeys.add(gridKey(grid));
      cards.push({
        grid,
        isWinner: false,
        winningNumber: null,
        winAmount: 0,
      });
    }
  }

  return shuffle(cards).map((card, idx) => ({
    ...card,
    cardNo: idx + 1,
    id: `CRD-${String(idx + 1).padStart(3, '0')}`,
  }));
}
